import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  MessageSquare, Pin, Trash2, Send, FileText, AlertCircle,
  HelpCircle, Info, Loader2, Image, Video
} from "lucide-react";

const POST_TYPES = {
  note: { label: "Note", icon: FileText, color: "bg-blue-100 text-blue-700" },
  instruction: { label: "Instruction", icon: Info, color: "bg-purple-100 text-purple-700" },
  update: { label: "Update", icon: AlertCircle, color: "bg-green-100 text-green-700" },
  question: { label: "Question", icon: HelpCircle, color: "bg-yellow-100 text-yellow-700" },
};

export function JobBoard({ projectId }) {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newContent, setNewContent] = useState("");
  const [newType, setNewType] = useState("note");
  const [posting, setPosting] = useState(false);

  const isManager = user?.role === "owner" || user?.role === "project_manager" || user?.role === "office_admin";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchPosts(); }, [projectId]);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/job-board/${projectId}`);
      setPosts(res.data || []);
    } catch (err) {
      // Error handled silently — non-critical operation
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!newContent.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/job-board`, {
        project_id: projectId,
        content: newContent.trim(),
        post_type: newType,
      });
      setNewContent("");
      setNewType("note");
      fetchPosts();
    } catch (err) {
      toast.error("Failed to post");
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/job-board/${postId}`);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot delete this post");
    }
  };

  const handlePin = async (postId, pinned) => {
    try {
      await axios.put(`${API}/job-board/${postId}`, { pinned: !pinned });
      fetchPosts();
    } catch (err) {
      toast.error("Failed to pin");
    }
  };

  const getInitials = (name) => name ? name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  return (
    <Card data-testid="job-board">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="w-4 h-4" />
          Job Board
          <Badge variant="outline" className="text-xs ml-auto">{posts.length} posts</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* New Post */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger className="w-32 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(POST_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="Write a note, instruction, update, or question..."
              rows={2}
              className="flex-1 text-sm resize-none"
              onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handlePost(); }}
              data-testid="job-board-input"
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">Ctrl+Enter to post</span>
            <Button size="sm" onClick={handlePost} disabled={posting || !newContent.trim()} data-testid="job-board-post-btn">
              {posting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
              Post
            </Button>
          </div>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No posts yet. Be the first to share!
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {posts.map(post => {
              const typeInfo = POST_TYPES[post.post_type] || POST_TYPES.note;
              const TypeIcon = typeInfo.icon;
              const canDelete = isManager || post.created_by === user?.user_id;
              const isManagerPost = post.created_by_role === "owner" || post.created_by_role === "project_manager" || post.created_by_role === "office_admin";

              return (
                <div
                  key={post.post_id}
                  className={`p-3 rounded-lg border ${post.pinned ? "border-amber-300 bg-amber-50/50" : "border-slate-200 bg-white"}`}
                  data-testid={`post-${post.post_id}`}
                >
                  <div className="flex items-start gap-2">
                    <Avatar className="w-7 h-7 shrink-0">
                      <AvatarFallback className="text-[10px] bg-slate-100">{getInitials(post.author?.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{post.author?.name || "Unknown"}</span>
                        <Badge className={`text-[10px] px-1 py-0 ${typeInfo.color}`}>
                          <TypeIcon className="w-2 h-2 mr-0.5 inline" />{typeInfo.label}
                        </Badge>
                        {isManagerPost && <Badge variant="outline" className="text-[10px] px-1 py-0">Manager</Badge>}
                        {post.pinned && <Pin className="w-3 h-3 text-amber-500" />}
                        <span className="text-[10px] text-slate-400 ml-auto">{new Date(post.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{post.content}</p>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      {isManager && (
                        <button onClick={() => handlePin(post.post_id, post.pinned)} className="p-1 hover:bg-slate-100 rounded" title={post.pinned ? "Unpin" : "Pin"}>
                          <Pin className={`w-3 h-3 ${post.pinned ? "text-amber-500" : "text-slate-300"}`} />
                        </button>
                      )}
                      {canDelete && !(post.created_by_role === "owner" && !isManager) && (
                        <button onClick={() => handleDelete(post.post_id)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
